/*
 * Generic scroll-snap carousel behaviour. Used by any section with a
 * [data-carousel] root containing a [data-carousel-track] (the scrollable
 * flex row), and optionally [data-carousel-prev] / [data-carousel-next]
 * buttons. No library - THEME-SPEC §6/§12: carousels are CSS scroll-snap,
 * arrows call scrollBy.
 *
 * Track parked mid-scroll: a [data-carousel] can set data-carousel-peek
 * (desktop, px) and data-carousel-peek-mobile (px, used below the 990px
 * breakpoint) to start scrolled in from the left edge, matching the Figma
 * "parked mid-scroll" treatment rather than starting flush at the first
 * card. The two differ because card/gutter widths differ per breakpoint.
 *
 * Looping: set data-carousel-loop="true" to wrap infinitely in both
 * directions instead of stopping at each end. The full real set is
 * cloned as aria-hidden duplicates and spliced onto both ends of the
 * track - [clone][real][clone] - so there is always a full set-width of
 * buffer to scroll into before hitting the track's physical end, no
 * matter how many cards fit in one viewport. (A smaller, fixed clone
 * count works on narrow viewports but silently fails to loop on wide
 * ones, because the browser clamps scrollLeft once the clone buffer runs
 * out before the wrap threshold is reached.) Once the real content's
 * width (one "set") is known, crossing into a clone zone silently snaps
 * scrollLeft back by exactly one set-width with scroll-behavior forced to
 * 'auto' - invisible, because the clone is pixel-identical to the real
 * card it's standing in for. Arrows never disable on a looping carousel.
 */
(() => {
  const MOBILE_QUERY = '(max-width: 989px)';
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const carousels = document.querySelectorAll('[data-carousel]');

  const cloneForLoop = (card) => {
    const clone = card.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('tabindex', '-1');
    clone.removeAttribute('id');
    clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
    clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((el) => {
      el.setAttribute('tabindex', '-1');
    });
    // A clone only ever shows as a sliver at the edge before the wrap jump
    // swaps it for the real card - never let it autoplay a second video.
    clone.querySelectorAll('video').forEach((el) => {
      el.removeAttribute('autoplay');
      el.pause();
    });
    return clone;
  };

  carousels.forEach((root) => {
    const track = root.querySelector('[data-carousel-track]');
    const prevBtn = root.querySelector('[data-carousel-prev]');
    const nextBtn = root.querySelector('[data-carousel-next]');
    if (!track) return;

    const loop = root.getAttribute('data-carousel-loop') === 'true';
    const realCards = Array.from(track.children);

    let setWidth = 0;
    let realStart = 0;

    if (loop && realCards.length > 0) {
      const headClones = realCards.map(cloneForLoop);
      const tailClones = realCards.map(cloneForLoop);

      // Bug #10 (FIX-PLAN-2026-09-11): inserting one clone at a time via
      // insertBefore(clone, track.firstElementChild) re-reads
      // firstElementChild on every iteration, which is the clone just
      // inserted the iteration before - so the left-side clones ended up
      // in reverse order (3, 2, 1 instead of 1, 2, 3), visible as a jump
      // when swiping back past the start. prepend() with the whole array
      // inserts them together, in argument order, in one call.
      track.prepend(...tailClones);
      headClones.forEach((clone) => track.appendChild(clone));

      realStart = realCards[0].offsetLeft;
      setWidth = headClones.length ? (headClones[0].offsetLeft - realStart) : 0;
    }

    const isMobile = window.matchMedia(MOBILE_QUERY).matches;
    const mobilePeek = root.getAttribute('data-carousel-peek-mobile');
    const peekAttr = (isMobile && mobilePeek !== null) ? mobilePeek : root.getAttribute('data-carousel-peek');
    const peek = parseInt(peekAttr || '0', 10);

    const firstCard = realCards[0] || track.firstElementChild;
    const step = () => (firstCard ? firstCard.getBoundingClientRect().width + 20 : track.clientWidth);
    const scrollBehavior = () => (REDUCED_MOTION ? 'auto' : 'smooth');

    if (loop) {
      // BUGS-2026-09-10-b #1: parking exactly at realStart - peek sits
      // inside the clone zone from the first frame, so a swipe back needs
      // zero pixels of movement to cross the wrap threshold - and a touch
      // browser cancels the pan the instant script touches scrollLeft
      // under the finger. Parking one full card past realStart (still
      // inset by the same peek amount, just relative to card 2) leaves a
      // full card's width of real scroll room before any wrap check could
      // fire.
      track.scrollLeft = realStart + step() - (peek > 0 ? peek : 0);
    } else if (peek > 0) {
      track.scrollLeft = peek;
    }

    // A track with its own scroll-padding-inline-start (mobile's
    // .modes__track/.reviews__track - see sections/modes.liquid,
    // sections/reviews.liquid) legitimately rests a few px before
    // realStart once CSS scroll-snap resolves the parked position
    // there - that is the intended left gutter, not a user having
    // scrolled backward into the clone buffer, so it must not trip
    // the backward-wrap branch below. Reads 0 wherever no
    // scroll-padding is set (desktop, unchanged), so this is a
    // no-op there - verified against the unpatched original with
    // an isolated harness reusing this exact file before landing.
    const scrollPaddingStart = parseFloat(getComputedStyle(track).scrollPaddingInlineStart) || 0;

    const wrapIfNeeded = () => {
      if (!loop || setWidth <= 0) return;
      if (track.scrollLeft >= realStart + setWidth) {
        track.style.scrollBehavior = 'auto';
        track.scrollLeft -= setWidth;
        track.style.scrollBehavior = '';
      } else if (track.scrollLeft < realStart - scrollPaddingStart) {
        track.style.scrollBehavior = 'auto';
        track.scrollLeft += setWidth;
        track.style.scrollBehavior = '';
      }
    };

    const updateArrows = () => {
      if (!prevBtn || !nextBtn) return;
      if (loop) {
        prevBtn.disabled = false;
        nextBtn.disabled = false;
        return;
      }
      const max = track.scrollWidth - track.clientWidth - 1;
      prevBtn.disabled = track.scrollLeft <= 0;
      nextBtn.disabled = track.scrollLeft >= max;
    };

    prevBtn?.addEventListener('click', () => {
      track.scrollBy({ left: -step(), behavior: scrollBehavior() });
    });

    nextBtn?.addEventListener('click', () => {
      track.scrollBy({ left: step(), behavior: scrollBehavior() });
    });

    // BUGS-2026-09-10-b #1: wrapIfNeeded must never run mid-gesture - it
    // did, on every scroll event, which is exactly what cancelled the
    // touch pan for a swipe back from the start. `scrollend` fires once
    // the browser considers scrolling truly finished (Safari 17.4+); a
    // ~150ms debounce covers browsers without it.
    const supportsScrollend = 'onscrollend' in window;
    let scrollEndTimer = null;

    track.addEventListener('scroll', () => {
      updateArrows();
      if (!supportsScrollend) {
        clearTimeout(scrollEndTimer);
        scrollEndTimer = setTimeout(wrapIfNeeded, 150);
      }
    }, { passive: true });

    if (supportsScrollend) {
      track.addEventListener('scrollend', wrapIfNeeded, { passive: true });
    }

    updateArrows();
  });
})();
